import { Outlet, Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { LanguageSelector } from './LanguageSelector'
import { cn } from '../utils/cn'
import { useAuth } from '../contexts/AuthContext'
import { AuthGuard } from './AuthGuard'

export function Layout() {
  const location = useLocation()
  const { t } = useTranslation()
  const { userData, logout } = useAuth()

  // If on login page, render without auth guard or sidebar
  if (location.pathname === '/login') {
    return <Outlet />
  }

  const navigation = [
    { name: t('navigation.dashboard'), href: '/', icon: '📊' },
    { name: t('navigation.challenges'), href: '/challenges', icon: '🎯' },
    { name: t('navigation.categories'), href: '/categories', icon: '📂' },
    { name: t('navigation.languages'), href: '/languages', icon: '🌐' },
    { name: t('navigation.users'), href: '/users', icon: '👥' },
    { name: t('navigation.analytics'), href: '/analytics', icon: '📈' },
  ]

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-50">
        {/* Fixed Sidebar */}
        <div className="fixed top-0 left-0 w-64 h-full bg-white shadow-sm border-r border-gray-200 overflow-y-auto">
          <div className="p-6">
            <h1 className="text-xl font-bold text-gray-900">StayChecked Admin</h1>
            {userData && (
              <div className="mt-3 text-sm text-gray-600">
                <div className="font-medium">{userData.displayName}</div>
                <div className="text-xs">{userData.email}</div>
                <div className="text-xs">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800 mt-1">
                    {userData.role}
                  </span>
                </div>
              </div>
            )}
          </div>
        
        <nav className="mt-6">
          <ul className="space-y-1 px-3">
            {navigation.map((item) => {
              // Check if current path matches the menu item exactly or is a nested route
              // Special case for home route to avoid matching all paths
              // Examples:
              // - /challenges -> matches /challenges (exact)
              // - /challenges/create -> matches /challenges (starts with)
              // - /challenges/123 -> matches /challenges (starts with)
              // - /categories/create -> matches /categories (starts with)
              const isActive = item.href === '/' 
                ? location.pathname === '/' 
                : location.pathname.startsWith(item.href)
              
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium transition-colors relative',
                      isActive
                        ? 'text-black font-bold border-l-4 border-gray-800'
                        : 'text-gray-700 hover:bg-gray-100'
                    )}
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.name}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Language Selector and Logout */}
        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex flex-col space-y-3">
            <LanguageSelector />
            <button
              onClick={logout}
              className="flex justify-center items-center px-3 py-2 rounded-full text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              {t('common.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Main content with left margin to avoid overlap */}
      <div className="ml-64">
        <Outlet />
      </div>
    </div>
    </AuthGuard>
  )
}